import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Panel } from "./panel";
import { WaterfallChart } from "./charts/waterfall-chart";
import { GroupedBarChart } from "./charts/grouped-bar-chart";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Skeleton } from "../../ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { grossMarginFor } from "../../pages/AthenaPricingEngine/lib/wal-ftp";
import {
  ALL_INDUSTRIES_LABEL,
  ALL_OFFICES_LABEL,
  ALL_RATINGS_LABEL,
  PLANNING_GRADES,
  PLANNING_TERMS,
  computeWal,
  useSimulatorResult,
  type SimulatorInputs,
} from "../../pages/AthenaPricingEngine/lib/api/planning";
import {
  useIndustries,
  useOffices,
  type Office,
} from "../../pages/AthenaPricingEngine/lib/api/reference";
import type { RiskGrade } from "../../pages/AthenaPricingEngine/lib/api/state-of-play";

type Components = {
  ftp: number;
  expectedLoss: number;
  opex: number;
  externalCommission: number;
  targetNetMargin: number;
  internalCommissionRate: number;
};

const MAX_FINANCED_AMOUNT = 250_000;

const COMPONENT_LABELS: Array<{ key: keyof Components; label: string; suffix?: string }> = [
  { key: "ftp", label: "FTP" },
  { key: "expectedLoss", label: "Expected Loss" },
  { key: "opex", label: "OpEx" },
  { key: "externalCommission", label: "External Commission" },
  { key: "targetNetMargin", label: "Target Net Margin" },
  { key: "internalCommissionRate", label: "Internal Commission Rate", suffix: "% of gross margin" },
];

/** The editable starting values a result gives the component list -- what a fresh run shows. */
function componentsFrom(result: Components): Components {
  return {
    ftp: result.ftp,
    expectedLoss: result.expectedLoss,
    opex: result.opex,
    externalCommission: result.externalCommission,
    targetNetMargin: result.targetNetMargin,
    internalCommissionRate: result.internalCommissionRate,
  };
}

function sameInputs(a: SimulatorInputs | null, b: SimulatorInputs): boolean {
  return (
    a != null &&
    a.office === b.office &&
    a.industry === b.industry &&
    a.grade === b.grade &&
    a.financedAmount === b.financedAmount &&
    a.termMonths === b.termMonths
  );
}

function fmtPct(value: number) {
  return `${value.toFixed(2)}%`;
}

function fmtMoney(value: number) {
  return value.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

/** Manage Targets identifies offices by name, not office_number -- resolves
 * that here rather than passing the raw number through (which never matched
 * anything on the other end). Omits a param entirely for "All Offices"/
 * "All Industry" rather than passing that literal string, since Manage
 * Targets' deep-link doesn't have an "All" concept of its own. */
function manageTargetsLinkParams(submitted: SimulatorInputs, offices: Office[]): string {
  const params = new URLSearchParams();
  const officeName = offices.find((o) => o.officeNumber === submitted.office)?.officeName;
  if (officeName) params.set("office", officeName);
  if (submitted.industry) params.set("industry", submitted.industry);
  return params.toString();
}

export function SimulatorPanel() {
  const officesQuery = useOffices();
  const offices = officesQuery.data ?? [];

  // Raw <Select> values -- may be a real office/industry/grade, or the
  // "All ..." sentinel, which is the default for all three. `office`/
  // `industryValue`/`gradeValue` below derive the real value (or undefined)
  // actually sent to the API.
  const [officeName, setOfficeName] = useState<string>(ALL_OFFICES_LABEL);
  const [industry, setIndustry] = useState<string>(ALL_INDUSTRIES_LABEL);
  const [grade, setGrade] = useState<string>(ALL_RATINGS_LABEL);
  const [financedAmount, setFinancedAmount] = useState(200_000);
  const [termMonths, setTermMonths] = useState(60);
  const [submitted, setSubmitted] = useState<SimulatorInputs | null>(null);
  const [overrides, setOverrides] = useState<Components | null>(null);

  const office =
    officeName === ALL_OFFICES_LABEL
      ? undefined
      : offices.find((o) => o.officeName === officeName)?.officeNumber;
  const industryValue = industry === ALL_INDUSTRIES_LABEL ? undefined : industry;
  const gradeValue = grade === ALL_RATINGS_LABEL ? undefined : (grade as RiskGrade);
  const amountTooHigh = financedAmount > MAX_FINANCED_AMOUNT;

  // Industries are scoped to whichever office is selected -- real
  // per-office calibration coverage, not a portfolio-wide list. Undefined
  // (All Offices) falls back to the portfolio-wide list.
  const industriesQuery = useIndustries(office);
  const industries = industriesQuery.data ?? [];

  const { data, isLoading, isError, error } = useSimulatorResult(submitted);

  useEffect(() => {
    if (data) setOverrides(componentsFrom(data));
  }, [data]);

  const grossMargin = overrides
    ? grossMarginFor(overrides.targetNetMargin, overrides.internalCommissionRate)
    : 0;
  const requiredYield = overrides
    ? Number(
        (
          overrides.ftp +
          overrides.expectedLoss +
          overrides.opex +
          overrides.externalCommission +
          grossMargin
        ).toFixed(2),
      )
    : 0;
  const internalCommission = overrides
    ? Number((grossMargin - overrides.targetNetMargin).toFixed(2))
    : 0;
  const internalCommissionDollars = submitted
    ? Math.round((internalCommission / 100) * submitted.financedAmount)
    : 0;
  const grossRav = overrides ? Number((grossMargin).toFixed(2)) : 0;
  const netRav = overrides ? Number(overrides.targetNetMargin.toFixed(2)) : 0;
  const netRavDollars = submitted ? Math.round((netRav / 100) * submitted.financedAmount) : 0;
  const wal =
    submitted && overrides ? computeWal(submitted.financedAmount, submitted.termMonths, requiredYield) : 0;
  const lifetimeLossPct = overrides ? Number((overrides.expectedLoss * wal).toFixed(2)) : 0;
  const lifetimeLossDollars = submitted
    ? Math.round((lifetimeLossPct / 100) * submitted.financedAmount)
    : 0;
  // = overrides.expectedLoss when wal > 0 (undoes the WAL scaling above) --
  // that's expected per the given formula, not a bug.
  const projectedAnnualizedLoss = wal > 0 && overrides ? Number((lifetimeLossPct / wal).toFixed(2)) : 0;
  const projectedAnnualizedLossDollars = submitted
    ? Math.round((projectedAnnualizedLoss / 100) * submitted.financedAmount)
    : 0;

  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)]">
      {/* Stays put while the results on the right scroll. top-[200px] clears the fixed header and step bar (main starts
          at the same 200px); the max-height keeps the whole form reachable on a short screen. */}
      <div className="lg:sticky lg:top-[200px] lg:max-h-[calc(100vh-220px)] lg:self-start lg:overflow-y-auto">
      <Panel title="Scenario" description="Price a hypothetical deal against approved policy">
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            const next: SimulatorInputs = {
              office,
              industry: industryValue,
              grade: gradeValue,
              financedAmount,
              termMonths,
            };
            if (sameInputs(submitted, next) && data) {
              // Same scenario again: the query key is unchanged, so no new result arrives and the effect above
              // would never refill `overrides` -- leaving the skeleton up forever. Reset to the result's own values.
              setOverrides(componentsFrom(data));
            } else {
              setOverrides(null);
            }
            setSubmitted(next);
          }}
        >
          <Field label="Office Name">
            <Select value={officeName} onValueChange={setOfficeName}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_OFFICES_LABEL}>{ALL_OFFICES_LABEL}</SelectItem>
                {offices.map((d) => (
                  <SelectItem key={d.officeNumber} value={d.officeName}>
                    {d.officeName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Industry">
            <Select value={industry} onValueChange={setIndustry}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_INDUSTRIES_LABEL}>{ALL_INDUSTRIES_LABEL}</SelectItem>
                {industries.map((i) => (
                  <SelectItem key={i} value={i}>
                    {i}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Grade">
            <Select value={grade} onValueChange={setGrade}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_RATINGS_LABEL}>{ALL_RATINGS_LABEL}</SelectItem>
                {PLANNING_GRADES.map((g) => (
                  <SelectItem key={g} value={g}>
                    {g}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Financed Amount">
            <Input
              type="number"
              min={0}
              max={MAX_FINANCED_AMOUNT}
              step="any"
              value={financedAmount}
              onChange={(event) => setFinancedAmount(Number(event.target.value))}
              className="h-9 text-sm"
            />
            {amountTooHigh ? (
              <p className="text-xs text-destructive">
                Financed amount can&apos;t exceed {fmtMoney(MAX_FINANCED_AMOUNT)}.
              </p>
            ) : null}
          </Field>

          <Field label="Term (Months)">
            <Select value={String(termMonths)} onValueChange={(value) => setTermMonths(Number(value))}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PLANNING_TERMS.map((t) => (
                  <SelectItem key={t} value={String(t)}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Button type="submit" className="w-full" disabled={amountTooHigh}>
            Run Scenario
          </Button>
        </form>
      </Panel>
      </div>

      <div className="space-y-6">
        {isError ? (
          <div
            role="alert"
            className="border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          >
            Could not run the scenario. {error instanceof Error ? error.message : ""}
          </div>
        ) : !submitted ? (
          <Panel title="Required Yield" description="Run a scenario to see the breakdown">
            <p className="text-sm text-muted-foreground">
              Choose an office name, industry, grade, amount, and term, then run the scenario.
            </p>
          </Panel>
        ) : isLoading || !overrides ? (
          <Panel title="Required Yield">
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-9 w-full" />
              ))}
            </div>
          </Panel>
        ) : (
          <>
            <Panel
              title="Required Yield"
              description={`${
                submitted.office != null
                  ? offices.find((o) => o.officeNumber === submitted.office)?.officeName ??
                    submitted.office
                  : ALL_OFFICES_LABEL
              } · ${submitted.industry ?? ALL_INDUSTRIES_LABEL} · ${
                submitted.grade ? `Grade ${submitted.grade}` : ALL_RATINGS_LABEL
              }`}
            >
              <WaterfallChart
                steps={[
                  { label: "FTP", value: overrides.ftp, color: "var(--grade-a)" },
                  {
                    label: "Expected Loss",
                    value: overrides.expectedLoss,
                    color: "var(--grade-b1)",
                  },
                  { label: "OpEx", value: overrides.opex, color: "var(--grade-b2)" },
                  {
                    label: "Ext Comm",
                    value: overrides.externalCommission,
                    color: "var(--grade-b3)",
                  },
                  // --approve rather than another --grade-*: the four cost components
                  // above already use the grade ramp, and margin is the one step that
                  // is earned rather than paid, so it reads better distinct from them.
                  { label: "Gross Margin", value: grossMargin, color: "var(--approve)" },
                ]}
                // hsl(...) is required, NOT bare var(--primary). --primary is stored as
                // a Tailwind HSL TRIPLET ("33 98% 58%"), so an SVG fill given it raw is
                // invalid and silently paints BLACK -- which is exactly how this bar and
                // Gross margin used to render. Every --grade-* token is a complete
                // hsl(...) colour, which is why those bars were fine.
                total={{ label: "Required Yield", color: "hsl(var(--primary))" }}
              />

              <div className="mt-5 divide-y divide-border border border-border">
                {/* Every component is editable, External commission included. Each starts at the
                    value Manage Targets shows for this scope (the server resolves the same
                    policy and context), and typing over one flows through requiredYield ->
                    WAL -> the waterfall and RAV charts. This is a test, not a policy change. */}
                {COMPONENT_LABELS.map(({ key, label, suffix }) => (
                  <div key={key} className="flex items-center justify-between gap-4 px-4 py-2.5">
                    <span className="text-sm text-muted-foreground">{label}</span>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="any"
                        value={overrides[key]}
                        onChange={(event) =>
                          setOverrides((prev) =>
                            prev ? { ...prev, [key]: Number(event.target.value) } : prev,
                          )
                        }
                        className="h-8 w-24 text-right text-sm tabular-nums"
                      />
                      <span className="text-xs text-muted-foreground">{suffix ?? "%"}</span>
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-between gap-4 px-4 py-2.5">
                  <span className="text-sm text-muted-foreground">
                    Gross Margin
                    <span className="ml-2 text-[10px] uppercase tracking-wider">Computed</span>
                  </span>
                  <span className="text-sm tabular-nums">{fmtPct(grossMargin)}</span>
                </div>
                <div className="flex items-center justify-between gap-4 bg-secondary px-4 py-3">
                  <span className="text-sm font-semibold uppercase tracking-wider">
                    Required Yield
                  </span>
                  <span className="text-lg font-semibold tabular-nums text-primary">
                    {fmtPct(requiredYield)}
                  </span>
                </div>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                This doesn&apos;t change anything live — it&apos;s a test. To actually set a target,
                go to{" "}
                <Link
                  to={`/risk-pricing-engine/manage-targets?${manageTargetsLinkParams(
                    submitted,
                    offices,
                  )}`}
                  className="underline underline-offset-2"
                >
                  Manage Targets
                </Link>
                .
              </p>
            </Panel>

            <Panel
              title="Commission And RAV"
              description="Gross RAV is margin before the RM is paid; net RAV is what the bank keeps"
            >
              {/* Three different quantities, not one quantity across three categories,
                  so each gets its own colour via cellColorKey: what the deal earns, what
                  is paid away, what is left. One flat colour made the split invisible --
                  and it painted black anyway, since bare var(--primary) is an HSL triplet
                  rather than a colour. */}
              <GroupedBarChart
                data={[
                  { label: "Expected Gross RAV", value: grossRav, color: "var(--grade-a)" },
                  {
                    label: "Internal Commission",
                    value: internalCommission,
                    color: "var(--grade-b2)",
                  },
                  { label: "Expected Net RAV", value: netRav, color: "var(--approve)" },
                ]}
                categoryKey="label"
                cellColorKey="color"
                series={[
                  { dataKey: "value", name: "Percent Of Deal", color: "hsl(var(--primary))" },
                ]}
                layout="horizontal-bars"
                categoryWidth={170}
                height={190}
              />
              <dl className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                <Metric label="Gross RAV" value={fmtPct(grossRav)} />
                <Metric label="Internal Commission" value={fmtPct(internalCommission)} />
                <Metric label="Internal Commission ($)" value={fmtMoney(internalCommissionDollars)} />
                <Metric label="Net RAV" value={fmtPct(netRav)} />
                <Metric label="Net RAV ($)" value={fmtMoney(netRavDollars)} />
              </dl>
              <p className="mt-3 text-xs text-muted-foreground">
                Internal commission is {overrides.internalCommissionRate.toFixed(1)}% of gross
                margin; external commission sits above the margin and never comes out of it.
              </p>
            </Panel>

            <Panel title="Scenario Loss Profile" description="Computed from this deal's own schedule">
              <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                <Metric label="WAL" value={`${wal.toFixed(2)} yrs`} />
                <Metric label="Projected Lifetime Loss" value={fmtPct(lifetimeLossPct)} />
                <Metric label="Projected Lifetime Loss ($)" value={fmtMoney(lifetimeLossDollars)} />
                <Metric label="Projected Annualized Loss" value={fmtPct(projectedAnnualizedLoss)} />
                <Metric label="Projected Annualized Loss ($)" value={fmtMoney(projectedAnnualizedLossDollars)} />
              </dl>
              <p className="mt-3 text-xs text-muted-foreground">
                Level-payment amortization over {submitted.termMonths} months on{" "}
                {fmtMoney(submitted.financedAmount)}; lifetime loss is the segment rate applied to
                this deal&apos;s own WAL, the same reverse calculation used in Segments.
              </p>
            </Panel>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="border border-border px-4 py-3">
      <dt className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1 text-lg font-semibold tabular-nums">{value}</dd>
    </div>
  );
}
